import { useRef, useState, useCallback, type RefObject } from 'react';

const MAX_HISTORY = 30;

export type DrawTool = 'brush' | 'eraser' | 'eyedropper' | 'select';

export interface UseDrawCanvasReturn {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  tool: DrawTool;
  color: string;
  lineWidth: number;
  historyLength: number;
  setTool: (t: DrawTool) => void;
  setColor: (c: string) => void;
  setLineWidth: (w: number) => void;
  startDrawing: (x: number, y: number) => void;
  continueDrawing: (x: number, y: number) => void;
  endDrawing: () => void;
  undo: () => void;
  clearAll: () => void;
}

export function useDrawCanvas(): UseDrawCanvasReturn {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // refs 避免 closure stale
  const toolRef = useRef<DrawTool>('brush');
  const colorRef = useRef('#ef4444');
  const lineWidthRef = useRef(4);
  const isDrawingRef = useRef(false);
  const historyRef = useRef<ImageData[]>([]);

  // state 供 UI re-render
  const [tool, setToolState] = useState<DrawTool>('brush');
  const [color, setColorState] = useState('#ef4444');
  const [lineWidth, setLineWidthState] = useState(4);
  const [historyLength, setHistoryLength] = useState(0);

  const setTool = useCallback((t: DrawTool) => {
    toolRef.current = t;
    setToolState(t);
  }, []);

  const setColor = useCallback((c: string) => {
    colorRef.current = c;
    setColorState(c);
  }, []);

  const setLineWidth = useCallback((w: number) => {
    lineWidthRef.current = w;
    setLineWidthState(w);
  }, []);

  const getCtx = useCallback(() => canvasRef.current?.getContext('2d') ?? null, []);

  const saveSnapshot = useCallback(() => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    if (historyRef.current.length >= MAX_HISTORY) {
      historyRef.current.shift();
    }
    historyRef.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    setHistoryLength(historyRef.current.length);
  }, [getCtx]);

  const startDrawing = useCallback((x: number, y: number) => {
    if (toolRef.current === 'eyedropper') return;
    const ctx = getCtx();
    if (!ctx) return;
    isDrawingRef.current = true;
    ctx.globalCompositeOperation =
      toolRef.current === 'eraser' ? 'destination-out' : 'source-over';
    ctx.strokeStyle = colorRef.current;
    ctx.lineWidth = lineWidthRef.current;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, [getCtx]);

  const continueDrawing = useCallback((x: number, y: number) => {
    if (!isDrawingRef.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [getCtx]);

  const endDrawing = useCallback(() => {
    if (!isDrawingRef.current) return;
    getCtx()?.closePath();
    isDrawingRef.current = false;
    saveSnapshot();
  }, [getCtx, saveSnapshot]);

  const undo = useCallback(() => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    historyRef.current.pop();
    setHistoryLength(historyRef.current.length);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (historyRef.current.length > 0) {
      ctx.putImageData(historyRef.current[historyRef.current.length - 1], 0, 0);
    }
  }, [getCtx]);

  const clearAll = useCallback(() => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    historyRef.current = [];
    setHistoryLength(0);
  }, [getCtx]);

  return {
    canvasRef, tool, color, lineWidth, historyLength,
    setTool, setColor, setLineWidth,
    startDrawing, continueDrawing, endDrawing,
    undo, clearAll,
  };
}
